import { useLoaderData } from '@remix-run/react';
import type { SerializeFrom } from '@remix-run/node';
import { ElementalPageProvider } from '@modules/elemental-page/context/ElementalPageContext';
import { ModalWrapper } from '@modules/elemental-page/component/modal-wrapper/ModalWrapper';
import ElementalModule from '@modules/elemental-page/component/elemental-module/ElementalModule';
import type { CommonModule } from '@modules/elemental-page/type';
import ElementalPageNavigation from '@modules/elemental-page/component/elemental-page-navigation/ElementalPageNavigation';
import { ModulePageThemeEnum } from '../../../../generated/graphql';
import ElementalPagePromo from '@modules/elemental-page/component/elemental-page-promo/ElementalPagePromo';
import {
    useAppDisplayModeContext,
    FlagSelector,
    useStyles,
} from '@archipro-design/aria';
import { useBoolean } from 'ahooks';
import { useRef } from 'react';
import { useFlagSlot } from '@archipro-website/top-navigation';
import { useRootData } from '@modules/root';
import type { loader } from '@modules/elemental-page/loaders/elemental-page-loader.server';

type ElementalPageLoader = SerializeFrom<typeof loader>;

const ElementalPage = () => {
    const {
        modules,
        pageTheme,
        pageTitle,
        libraryItemId,
        promo,
        backgroundColor,
    } = useLoaderData<ElementalPageLoader>();
    const { css } = useStyles({ backgroundColor });
    const navigation = modules
        .filter(
            (module) =>
                !!module.anchor?.length && !!module.navigationLabel?.length
        )
        .map(({ anchor, navigationLabel }) => ({
            anchor: anchor!,
            navigationLabel: navigationLabel!,
        }));

    const appDisplayMode = useAppDisplayModeContext();
    const menuActionsState = useBoolean(false);
    const flagMenuState = useBoolean(false);
    const flagRed = useRef<HTMLDivElement>(null);
    const { env } = useRootData();
    const flagProps = useFlagSlot(
        appDisplayMode,
        flagRed,
        menuActionsState,
        flagMenuState,
        env
    );

    const flagSelector = <FlagSelector ref={flagRed} flagSlot={flagProps} />;

    const enableElementalPageNavigation =
        pageTheme === ModulePageThemeEnum.About;

    return (
        <ElementalPageProvider>
            <div
                className={css({
                    backgroundColor: backgroundColor ?? undefined,
                })}
            >
                <ModalWrapper>
                    {enableElementalPageNavigation && (
                        <ElementalPageNavigation
                            pageTitle={pageTitle}
                            navigationItems={navigation}
                        />
                    )}
                    {enableElementalPageNavigation &&
                        !appDisplayMode.mobile &&
                        promo && (
                            <ElementalPagePromo
                                promo={promo}
                                flagSelector={flagSelector}
                                libraryItemId={libraryItemId}
                            />
                        )}
                    {modules?.map((module: CommonModule, index) => {
                        return (
                            <ElementalModule
                                item={module}
                                pageTheme={pageTheme}
                                key={index}
                            />
                        );
                    })}
                </ModalWrapper>
            </div>
        </ElementalPageProvider>
    );
};

export default ElementalPage;
